import { useCallback } from 'react';

import { trackRadarOrderPlaced } from '@sg/radar-fe';

import { sumLedgerTotal } from '@order/features-ordering';
import { type OrderingContext } from '@order/features-ordering';
import { useAppsFlyer } from '@order/integrations-appsflyer';
import {
  formatBagForAnalytics,
  formatCustomerPersonalInfo,
  googleAnalytics,
} from '@order/Telemetry';

import { useSnapPixel } from '../../../../integrations';

export const usePostOrderTracking = () => {
  const { logPurchaseEvent: trackAppsFlyerPurchase } = useAppsFlyer();
  const { trackPurchase: trackSnapPixelPurchase } = useSnapPixel();

  const handlePostOrderTracking = useCallback(
    ({ context }: { context: OrderingContext }) => {
      const { orderId = '', cart, selectedTip, availableCredit, shouldUseCredit } = context;

      const { lineItems, ledger } = cart;

      const { total } = sumLedgerTotal(ledger, selectedTip, availableCredit, shouldUseCredit);

      const orderValue = total / 100;

      trackSnapPixelPurchase({
        priceInDollars: orderValue,
        orderId,
        productIds: lineItems?.map((item) => item.product?.id ?? '') ?? [],
        lineItemsQuantity: lineItems?.length ?? 0,
      });

      const telemetryBag = formatTelemetryBag(context, orderValue);

      googleAnalytics.track({
        event: 'purchase',
        userId: context?.customer?.customerId,
        transaction_id: orderId,
        new_customer: context?.customer?.isNewCustomer,
        ...formatBagForAnalytics(telemetryBag),
        ...formatCustomerPersonalInfo(telemetryBag),
      });

      trackAppsFlyerPurchase({
        orderId,
        orderValue: telemetryBag.orderValue,
        orderType: telemetryBag.orderType,
        bagCount: telemetryBag.bagCount,
        productIds: telemetryBag.productIds,
      });

      const deliveryAddress =
        cart.orderChannel === 'delivery' ? cart.deliveryOrderDetail?.address : undefined;

      trackRadarOrderPlaced({
        orderId,
        orderType: telemetryBag.orderType,
        revenue: telemetryBag.orderValue,
        restaurantSlug: cart.restaurantSlug ?? '',
        restaurantEntityId: cart.restaurantEntityId ?? '',
        restaurantName: cart.restaurantName ?? '',
        deliveryLocation: deliveryAddress
          ? {
              latitude: deliveryAddress.latitude,
              longitude: deliveryAddress.longitude,
              street: deliveryAddress.street,
              city: deliveryAddress.city,
              state: deliveryAddress.state,
              zipCode: deliveryAddress.zipCode,
            }
          : undefined,
      });
    },
    [trackSnapPixelPurchase, trackAppsFlyerPurchase],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { handlePostOrderTracking };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTelemetryBag(context: OrderingContext, orderValue: number) {
  const products =
    context.cart?.lineItems?.map((item) => ({
      id: item.product?.id ?? '',
      name: item.product?.name ?? '',
      quantity: item.quantity ?? 0,
      cost: item.cost ?? 0,
    })) ?? [];

  return {
    orderValue,
    products,
    productIds: products.map((item) => item.id).join(','),
    bagCount: products.reduce((acc, item) => acc + (item.quantity ?? 0), 0),
    restaurantId: context?.cart?.restaurantId,
    restaurantName: context?.cart?.locationName,
    orderId: context?.orderId,
    orderType: context?.cart?.orderChannel,
    street: context?.cart?.deliveryOrderDetail?.address?.street,
    city: context?.cart?.deliveryOrderDetail?.address?.city,
    state: context?.cart?.deliveryOrderDetail?.address?.state,
    zipCode: context?.cart?.deliveryOrderDetail?.address?.zipCode,
    country: context?.cart?.deliveryOrderDetail?.address?.country,
    customer: {
      firstName: context?.customer?.firstName,
      lastName: context?.customer?.lastName,
      email: context?.customer?.email,
      phoneNumber: context?.customer?.phoneNumber,
    },
  };
}
